import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  Row,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { createColumns } from "../../columns";

import { cn } from "@/libs/utils";
import EstimateBadge from "../EstimateBadge";
import { BadgeKeys, BadgeType } from "../../constants/badgeTypes";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import { ReactComponent as PlusCircle } from "@/assets/images/plus-background.svg";
import {
  useAllRows,
  useExpandedRowIds,
  useFilteredRows,
  useIsEditing,
  useIsExcelDownloading,
  useSetExpandedRowId,
  useSetIsEditing,
  useEstimateOriginalRows,
  useHasEstimateOriginalData,
  useSetEstimateOriginalRows,
} from "@/pages/Project/Estimate/hooks/useTableStore";
import EstimateAlternativeProductTable from "./EstimateAlternativeProductTable";
import { CombinedRow, SmfProduct, ItemSheetOriginal } from "@/types/itemsheet";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { useSelectedProjectInfo } from "../../hooks/useTabStore";
import {
  useChangeToSmfProductId,
  useChangeToTppProductId,
  useUpdateProjectGroupsItemSheetLatest,
  useHighlightProjectGroupsItemsheet,
  useReadProjectGroupsItemSheetOriginalInfo,
} from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/libs/react-query/queryKeys";
import { useParams } from "react-router-dom";
import { showToast } from "@/utils/toastUtils";
import EditableCell from "./EstimateEditableCell";
import EditableHeader from "./EstimateEditableHeader";
import { useEstimateHistoryStore } from "../../hooks/useHistoryStore";
import SearchProductDialog from "../SearchProductDialog";
import { ItemSheetExtraTag } from "@/consts/Estimate";
import { parsePrice } from "@/pages/Bid/Analytics/utils";
import { parseItemSheetObjectToRows } from "../../utils";
import { ProjectType } from "@/consts/Project";

type Props = {
  rowSelection: Record<number, boolean>;
  setRowSelection: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  selectedOriginal: ItemSheetOriginal; // Original Data 추가
};

export default function EstimateProductTable({
  rowSelection,
  setRowSelection,
  selectedOriginal, // Original Data prop 추가
}: Props) {
  const methods = useForm<{ rows: CombinedRow[] }>({
    defaultValues: { rows: [] },
  });
  const { control, handleSubmit, reset, setValue } = methods;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "rows",
  });
  const allRows = useAllRows();
  const filteredRows = useFilteredRows(); // Latest Data

  const { group_id = "" } = useParams() || {};
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    actions: false,
  });
  const [extraKeyLabels, setExtraKeyLabels] = useState<Record<string, string>>(
    {}
  );
  const [extraKeys, setExtraKeys] = useState<string[]>([]);
  const allRowsRef = useRef<CombinedRow[]>([]);
  const originalExtraKeysRef = useRef<string[]>([]);
  const originalExtraKeyLabelsRef = useRef<Record<string, string>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null
  );
  // 강제 리렌더링을 위한 상태
  const [forceUpdate, setForceUpdate] = useState(0);

  // 가상화를 위한 ref
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const selectedProjectInfo = useSelectedProjectInfo();
  const {
    latestSheetId = "",
    projectId = "",
    projectType,
  } = selectedProjectInfo || {};

  // 🔥 Original Data API로 별도 호출
  const { data: originalData } = useReadProjectGroupsItemSheetOriginalInfo(
    group_id,
    {
      project_id: projectId,
      project_type: projectType || ProjectType.PROJ,
    }
  );

  // 🔥 Original Data: API 응답에서 파싱
  const originalRows = useMemo(() => {
    if (!originalData?.original?.data) {
      return [];
    }

    return parseItemSheetObjectToRows(originalData.original.data);
  }, [originalData]);
  const { mutate } = useUpdateProjectGroupsItemSheetLatest(
    group_id,
    latestSheetId
  );
  const { mutate: changeSmfMutate } = useChangeToSmfProductId(
    group_id,
    latestSheetId
  );
  const { mutate: changeTppMutate } = useChangeToTppProductId(
    group_id,
    latestSheetId
  );
  const { mutateAsync: highlightMutateAsync } =
    useHighlightProjectGroupsItemsheet(group_id, latestSheetId);

  // get zustand store state
  const expandedRowIds = useExpandedRowIds();
  const isEditing = useIsEditing();
  const isExcelDownloading = useIsExcelDownloading();

  const setIsEditing = useSetIsEditing();
  const setExpandedRowId = useSetExpandedRowId();

  const openDialog = (rowIndex: number, productId: string) => {
    setSelectedRowIndex(rowIndex);
    setSelectedProductId(productId);
    setDialogOpen(true);
  };

  const generateNextExtraKey = () => {
    const currentIndices = extraKeys
      .map((key) => {
        const match = key.match(/^추가_(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((v): v is number => v !== null);

    const nextIndex = currentIndices.length
      ? Math.max(...currentIndices) + 1
      : 0;

    return `추가_${nextIndex}`;
  };

  const handleAddExtraColumn = (key: string, defaultValue: any = "") => {
    fields.forEach((_, index) => {
      const updated = {
        ...fields[index],
        extra: { ...fields[index].extra, [key]: defaultValue },
      };
      update(index, updated);
    });
    setExtraKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setExtraKeyLabels((prev) => ({ ...prev, [key]: key }));
    useEstimateHistoryStore.getState().push(methods.getValues("rows"));
  };

  const handleRowDelete = (uuid: string) => {
    const rowIndex = fields.findIndex((row) => row.uuid === uuid);
    remove(rowIndex);
    const updated = methods
      .getValues("rows")
      .filter((row) => row.uuid !== uuid);
    reset({ rows: updated }, { keepDirtyValues: true });
    useEstimateHistoryStore.getState().push(methods.getValues("rows"));
  };

  const handleAddRow = () => {
    append({
      uuid: null,
      seq_no: null,
      product_name: "",
      product_id: "",
      manufacturer: "",
      vendor: "",
      buyer: "",
      specification: "",
      unit: "",
      quantity: null,
      unit_cost: null,
      list_price: null,
      total_cost: null,
      total_list_price: null,
      tags: [],
      extra: {},
      googleSearchResult: [],
      purchaseHistory: [],
      list_num: null,
    });
    useEstimateHistoryStore.getState().push(methods.getValues("rows"));
  };

  const handleApplyTppProduct = (rowIndex: number) => {
    if (rowIndex === null || !selectedProjectInfo) return;

    const selectedRow = fields[rowIndex];
    const { projectId, projectType, originalId } = selectedProjectInfo;
    const dataId = selectedRow.uuid!;

    const basePayload = {
      project_id: projectId,
      project_type: projectType,
      original_id: originalId,
      data_uuid: dataId,
      product_id: selectedRow.product_id || "",
    };

    const applyUpdate = (updated: Partial<CombinedRow>) => {
      update(rowIndex, { ...selectedRow, ...updated });
      useEstimateHistoryStore.getState().push(methods.getValues("rows"));
    };

    changeTppMutate(basePayload, {
      onSuccess: () => {
        applyUpdate({
          vendor: "",
          buyer: "",
          unit_cost: null,
          list_price: null,
          tags: [],
        });
      },
      onError: (error: any) => {
        showToast({
          type: "error",
          message: "타사 제품으로 분류 실패",
          description:
            error?.response?.data?.display_message ||
            "서버와 통신 중 오류가 발생했습니다.",
        });
      },
    });
  };

  const columns = useMemo(() => {
    const baseCols = createColumns(
      isEditing,
      isExcelDownloading,
      expandedRowIds,
      setExpandedRowId,
      handleRowDelete,
      handleAddExtraColumn,
      generateNextExtraKey,
      setExtraKeyLabels,
      openDialog,
      handleApplyTppProduct,
      originalRows // 🔥 Original Data 전달
    );

    const extraCols = extraKeys.map((key) => ({
      id: `extra.${key}`,
      header: () =>
        isEditing ? (
          <EditableHeader
            label={extraKeyLabels[key] ?? key}
            onChange={(val) =>
              setExtraKeyLabels((prev) => ({
                ...prev,
                [key]: val,
              }))
            }
          />
        ) : (
          <span>{extraKeyLabels[key] ?? key}</span>
        ),
      cell: ({ row }: { row: Row<CombinedRow> }) => {
        // 🔥 Original Data에서 해당 행의 원본 값 찾기
        const originalRow = originalRows.find(
          (origRow: CombinedRow) => origRow.uuid === row.original.uuid
        );
        const originalValue = originalRow?.extra?.[key] ?? "";

        return (
          <EditableCell
            isEditing={isEditing}
            value={row.original.extra?.[key] ?? ""}
            rowIndex={row.index}
            accessorKey={`extra.${key}` as keyof CombinedRow}
            originalValue={originalValue}
          />
        );
      },
      size: 150,
    }));

    const insertAt = baseCols.findIndex((col) => col.id === "actions");
    return [
      ...baseCols.slice(0, insertAt),
      ...extraCols,
      ...baseCols.slice(insertAt),
    ];
  }, [
    isEditing,
    isExcelDownloading,
    expandedRowIds,
    extraKeys,
    extraKeyLabels,
    originalRows, // 의존성 추가
    forceUpdate,
  ]);

  // useWatch로 실제 폼 상태 감지
  const watchedRows = useWatch({ control, name: "rows" }) as CombinedRow[];

  // watchedRows를 기반으로 테이블 데이터 생성 (참조 변경 보장)
  const tableData = useMemo(() => watchedRows || [], [watchedRows]);

  const table = useReactTable({
    data: tableData,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: Number.MAX_SAFE_INTEGER,
      },
    },
  });

  // 가상화 설정
  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: useCallback(() => 68, []),
    overscan: 30,
    scrollPaddingStart: 0,
    scrollPaddingEnd: 0,
    getItemKey: useCallback(
      (index: number) => rows[index]?.original?.uuid || index,
      [rows]
    ),
    initialOffset: 0,
    debug: false,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  // EXCEL 다운로드 시 전체 선택
  useEffect(() => {
    if (isExcelDownloading) {
      const allRows = table.getRowModel().rows;
      const allSelected: Record<number, boolean> = {};
      allRows.forEach((row) => {
        allSelected[row.index] = true;
      });
      setRowSelection(allSelected);
    } else {
      setRowSelection({});
    }
  }, [isExcelDownloading, table]);

  // 전체 행 ref 에 저장
  useEffect(() => {
    if (allRows.length > 0) {
      allRowsRef.current = [...allRows];
    }
  }, [allRows]);

  // 필터링 된 rows 로 테이블 초기화
  useEffect(() => {
    // 필터링된 행 데이터도 깊은 복사본 사용하여 원본 데이터 보호
    const initData = filteredRows.map((row) => ({
      ...row,
      extra: row.extra ? { ...row.extra } : {},
      extras: row.extras ? { ...row.extras } : undefined,
      tags: row.tags ? [...row.tags] : [],
      googleSearchResult: row.googleSearchResult
        ? [...row.googleSearchResult]
        : [],
      purchaseHistory: row.purchaseHistory ? [...row.purchaseHistory] : [],
    }));

    reset({ rows: initData });

    const keySet = new Set<string>();
    const labelMap: Record<string, string> = {};
    filteredRows.forEach((row) => {
      Object.keys(row.extra ?? {}).forEach((key) => {
        keySet.add(key);
        if (!labelMap[key]) labelMap[key] = key;
      });
    });
    setExtraKeys(Array.from(keySet));
    setExtraKeyLabels((prev) => ({ ...labelMap, ...prev }));
  }, [filteredRows]);

  // 🔥 Original Data에서 extra 키 추출
  useEffect(() => {
    if (originalRows.length > 0) {
      const keySet = new Set<string>();
      const labelMap: Record<string, string> = {};

      originalRows.forEach((row) => {
        Object.keys(row.extra ?? {}).forEach((key) => {
          keySet.add(key);
          if (!labelMap[key]) labelMap[key] = key;
        });
      });

      // Original Data의 extra 키들을 기본값으로 설정
      originalExtraKeysRef.current = Array.from(keySet);
      originalExtraKeyLabelsRef.current = labelMap;
    }
  }, [originalRows]);

  // 편집 모드 진입 시 설정
  useEffect(() => {
    if (isEditing) {
      // Latest Data로 폼 초기화
      const formInitData = filteredRows.map((row) => ({
        ...row,
        extra: row.extra ? { ...row.extra } : {},
        extras: row.extras ? { ...row.extras } : undefined,
        tags: row.tags ? [...row.tags] : [],
        googleSearchResult: row.googleSearchResult
          ? [...row.googleSearchResult]
          : [],
        purchaseHistory: row.purchaseHistory ? [...row.purchaseHistory] : [],
      }));

      reset({ rows: formInitData }, { keepDirtyValues: true });
      useEstimateHistoryStore.getState().reset();
      useEstimateHistoryStore.getState().push(formInitData);

      // 하이라이트된 행들을 자동으로 체크 (항상 수행)
      const highlightedRows: Record<number, boolean> = {};
      filteredRows.forEach((row, index) => {
        if (row.extras?.highlight === true) {
          highlightedRows[index] = true;
        }
      });
      if (Object.keys(highlightedRows).length > 0) {
        setRowSelection(highlightedRows);
      } else {
        setRowSelection({}); // 하이라이트된 행이 없으면 선택 초기화
      }
    }
  }, [isEditing, filteredRows, originalRows]);

  // 편집 모드 시 actions 열, extra 열 보이기
  useEffect(() => {
    table.setColumnVisibility((prev) => ({
      ...prev,
      actions: isEditing,
      ...Object.fromEntries(extraKeys.map((key) => [`extra.${key}`, true])),
    }));
  }, [isEditing, extraKeys, table]);

  const handleHighlightSubmit = async () => {
    if (!selectedProjectInfo) return;
    const { projectId, projectType, originalId } = selectedProjectInfo;

    // 선택된 행들에 대해 하이라이트 적용
    const selectedRowIndices = Object.keys(rowSelection).filter(
      (key) => rowSelection[parseInt(key)]
    );

    if (selectedRowIndices.length > 0) {
      const highlightActions = selectedRowIndices
        .map((index) => {
          const rowIndex = parseInt(index);
          const selectedRow = fields[rowIndex];

          if (selectedRow?.uuid) {
            return {
              uuid: selectedRow.uuid,
              highlight: true, // 체크된 행은 하이라이트 적용
              rowIndex: rowIndex,
            };
          }
          return null;
        })
        .filter(Boolean);

      // 체크 해제된 행들 (이전에 하이라이트되었지만 현재 체크되지 않은 행들)
      const unhighlightActions: Array<{
        uuid: string;
        highlight: boolean;
        rowIndex: number;
      }> = [];
      fields.forEach((row, index) => {
        const isCurrentlySelected = rowSelection[index];
        const wasHighlighted = row.extras?.highlight === true;

        if (!isCurrentlySelected && wasHighlighted && row.uuid) {
          unhighlightActions.push({
            uuid: row.uuid,
            highlight: false, // 하이라이트 제거
            rowIndex: index,
          });
        }
      });

      const allHighlightActions = [...highlightActions, ...unhighlightActions];

      // 순차 처리로 안정성 확보 (race condition 방지)
      const results: Array<{ status: "fulfilled" | "rejected"; action: any }> =
        [];
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < allHighlightActions.length; i++) {
        const action = allHighlightActions[i];
        if (!action) {
          results.push({ status: "fulfilled", action: null });
          continue;
        }

        // 최대 3번 재시도
        let retryCount = 0;
        let success = false;

        while (retryCount < 3 && !success) {
          try {
            if (retryCount > 0) {
              await new Promise((resolve) =>
                setTimeout(resolve, 200 * retryCount)
              ); // 재시도 간 지연 단축 (200ms씩 증가)
            }

            await highlightMutateAsync({
              project_id: projectId,
              project_type: projectType,
              original_id: originalId,
              data_uuid: action.uuid,
              highlight: action.highlight,
            });

            // API 성공 즉시 해당 행의 UI 업데이트 (노랑색 배경 즉시 적용)
            const currentValues = methods.getValues("rows");
            const updatedValues = currentValues.map((row) => {
              if (row.uuid === action.uuid) {
                return {
                  ...row,
                  extras: {
                    ...row.extras,
                    highlight: action.highlight,
                  },
                } as CombinedRow;
              }
              return row;
            });
            methods.setValue("rows", updatedValues);
            setForceUpdate((prev) => prev + 1); // 즉시 리렌더링

            results.push({ status: "fulfilled", action });
            successCount++;
            success = true;

            // API 호출 간 짧은 지연 (50ms로 단축)
            if (i < allHighlightActions.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 50));
            }
          } catch (error) {
            retryCount++;

            if (retryCount >= 3) {
              results.push({ status: "rejected", action });
              failedCount++;
              showToast({
                type: "error",
                message: "하이라이트 적용 실패",
                description: `행 ${action.rowIndex + 1}의 하이라이트 적용에 실패했습니다. (3회 재시도 후 실패)`,
              });
            }
          }
        }
      }

      if (failedCount > 0) {
        showToast({
          type: "warning",
          message: "일부 하이라이트 적용 실패",
          description: `${failedCount}개 항목의 하이라이트 적용에 실패했습니다.`,
        });
      }

      // 개별 UI 업데이트가 완료되었으므로 최종 상태만 확인
      const successfulActions = results
        .filter(
          (
            result
          ): result is { status: "fulfilled"; action: NonNullable<any> } =>
            result.status === "fulfilled" && result.action !== null
        )
        .map((result) => result.action);

      // 최종 UI 강제 업데이트 (혹시 모를 누락 방지)
      setForceUpdate((prev) => prev + 1);

      // 성공 메시지
      if (successCount > 0) {
        const appliedCount = successfulActions.filter(
          (a) => a.highlight
        ).length;
        const removedCount = successfulActions.filter(
          (a) => !a.highlight
        ).length;

        let description = "";
        if (appliedCount > 0 && removedCount > 0) {
          description = `${appliedCount}개 항목에 노랑색 배경 적용, ${removedCount}개 항목에서 해제되었습니다.`;
        } else if (appliedCount > 0) {
          description = `${appliedCount}개 항목에 노랑색 배경이 적용되었습니다.`;
        } else if (removedCount > 0) {
          description = `${removedCount}개 항목에서 노랑색 배경이 해제되었습니다.`;
        }

        showToast({
          type: "success",
          message: "하이라이트 처리 완료",
          description,
        });
      }

      return;
    }
  };

  const onSubmit = async (values: { rows: CombinedRow[] }) => {
    if (!selectedProjectInfo) return;
    const { projectId, projectType, originalId } = selectedProjectInfo;

    // 하이라이트 처리 (선택된 행이 있는 경우)
    const selectedRowIndices = Object.keys(rowSelection).filter(
      (key) => rowSelection[parseInt(key)]
    );

    let highlightProcessed = false;
    if (selectedRowIndices.length > 0) {
      try {
        await handleHighlightSubmit();
        highlightProcessed = true;
      } catch (error) {
        showToast({
          type: "error",
          message: "하이라이트 처리 실패",
          description:
            "하이라이트 처리 중 오류가 발생했습니다. 데이터 저장은 계속 진행됩니다.",
        });
      }
    }

    // 일반 데이터 업데이트 처리
    const editedMap = new Map(values.rows.map((r) => [r.uuid, r]));
    const mergedRows = allRowsRef.current.map((originalRow) => {
      const edited = editedMap.get(originalRow.uuid);
      return edited ? { ...originalRow, ...edited } : originalRow;
    });

    const added = values.rows.filter(
      (row) => !allRowsRef.current.some((o) => o.uuid === row.uuid)
    );

    const filteredUuids = filteredRows.map((r) => r.uuid);
    const deletedUuids = filteredUuids.filter(
      (uuid) => !values.rows.some((r) => r.uuid === uuid)
    );

    const finalData = [
      ...mergedRows.filter((row) => !deletedUuids.includes(row.uuid)),
      ...added,
    ];

    const remappedFinalData = finalData.map((row) => {
      // extras 필드 제거 (서버 API에서 기대하지 않는 필드)
      const { extras, ...rowWithoutExtras } = row;

      let calculatedTotalListPrice = null;
      let calculatedTotalCost = null;

      if (
        rowWithoutExtras.quantity != null &&
        rowWithoutExtras.list_price != null
      ) {
        const listPrice =
          typeof rowWithoutExtras.list_price === "string"
            ? parseFloat(rowWithoutExtras.list_price)
            : rowWithoutExtras.list_price;
        calculatedTotalListPrice = rowWithoutExtras.quantity * listPrice;
      }

      if (
        rowWithoutExtras.quantity != null &&
        rowWithoutExtras.unit_cost != null
      ) {
        const unitCost =
          typeof rowWithoutExtras.unit_cost === "string"
            ? parseFloat(rowWithoutExtras.unit_cost)
            : rowWithoutExtras.unit_cost;
        calculatedTotalCost = rowWithoutExtras.quantity * unitCost;
      }

      // extra 필드 처리
      let processedExtra = rowWithoutExtras.extra;
      if (rowWithoutExtras.extra) {
        const newExtra: Record<string, any> = {};
        for (const [key, value] of Object.entries(rowWithoutExtras.extra)) {
          const displayKey = extraKeyLabels[key];
          newExtra[displayKey && displayKey.trim() !== "" ? displayKey : key] =
            value;
        }
        processedExtra = newExtra;
      }

      return {
        ...rowWithoutExtras,
        total_list_price: calculatedTotalListPrice,
        total_cost: calculatedTotalCost,
        extra: processedExtra,
      };
    });

    mutate(
      {
        project_id: projectId,
        project_type: projectType,
        original_id: originalId,
        data: remappedFinalData,
      },
      {
        onSuccess: () => {
          const hasDataChanges =
            Object.keys(methods.formState?.dirtyFields || {}).length > 0;

          let message = "저장 완료";
          let description = "";

          if (highlightProcessed && hasDataChanges) {
            description = "하이라이트 및 데이터 변경사항이 저장되었습니다.";
          } else if (highlightProcessed) {
            description = "하이라이트 변경사항이 저장되었습니다.";
          } else if (hasDataChanges) {
            description = "데이터 변경사항이 저장되었습니다.";
          } else {
            description = "변경사항이 저장되었습니다.";
          }

          showToast({
            message,
            description,
            type: "success",
          });

          // 저장 성공 시 편집 모드 종료
          setIsEditing(false);
          setRowSelection({}); // 체크박스 선택 상태 초기화

          // 저장 성공 시 편집 모드 종료 및 Latest Data 자동 갱신
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.PROJECT_GROUPS.ITEM_SHEETS.LATEST(
              group_id,
              latestSheetId
            ),
          });

          originalExtraKeysRef.current = [...extraKeys];
          originalExtraKeyLabelsRef.current = { ...extraKeyLabels };
          setForceUpdate((prev) => prev + 1);
        },
        // 실패 로직
        onError: (error: any) => {
          showToast({
            type: "error",
            message: "저장 실패",
            description:
              error?.response?.data?.display_message ||
              "서버와 통신 중 오류가 발생했습니다.",
          });
        },
      }
    );
  };

  const handleUndo = () => {
    const prev = useEstimateHistoryStore.getState().undo();
    if (prev) reset({ rows: prev }, { keepDirtyValues: true });
  };

  const handleRedo = () => {
    const next = useEstimateHistoryStore.getState().redo();
    if (next) reset({ rows: next }, { keepDirtyValues: true });
  };

  const handleApplyProduct = (product: SmfProduct) => {
    if (selectedRowIndex === null || !selectedProjectInfo) return;

    const selectedRow = fields[selectedRowIndex];
    const unitCost = product.unit_cost || null;
    const parsedUnitCost = unitCost == null ? unitCost : parsePrice(unitCost);

    const { projectId, projectType, originalId } = selectedProjectInfo;

    const dataId = selectedRow.uuid!;
    const baseMutationPayload = {
      project_id: projectId,
      project_type: projectType,
      original_id: originalId,
      data_uuid: dataId,
      product_id: product.id,
    };

    const applyUpdate = (updated: Partial<CombinedRow>) => {
      update(selectedRowIndex, { ...selectedRow, ...updated });
      useEstimateHistoryStore.getState().push(methods.getValues("rows"));
    };

    changeSmfMutate(
      {
        ...baseMutationPayload,
        division: product.division,
        unit_cost: parsedUnitCost,
        list_price: product.list_price,
      },
      {
        onSuccess: () => {
          applyUpdate({
            product_id: product.id,
            vendor: "Thermo Fisher",
            buyer: product.division,
            unit_cost: parsedUnitCost,
            list_price: product.list_price,
            tags: [ItemSheetExtraTag.OWN_PRODUCT],
          });
        },
        onError: (error: any) => {
          showToast({
            type: "error",
            message: "자사 제품으로 변경 실패",
            description:
              error?.response?.data?.display_message ||
              "서버와 통신 중 오류가 발생했습니다.",
          });
        },
      }
    );
  };

  return (
    <Form {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="rounded-md shadow-xl relative">
          {isEditing ? (
            <>
              {/* TODO: redo, undo 기술 구현 */}

              <div className="absolute right-0 -top-10 flex items-center justify-end gap-4">
                <Button
                  type="reset"
                  className="bg-pink hover:bg-pink/80"
                  onClick={() => {
                    // 취소 시 Latest Data로 복원 (변경사항 취소)
                    const resetData = filteredRows.map((row: any) => ({
                      ...row,
                      extra: row.extra ? { ...row.extra } : {},
                      extras: row.extras ? { ...row.extras } : undefined,
                      tags: row.tags ? [...row.tags] : [],
                      googleSearchResult: row.googleSearchResult
                        ? [...row.googleSearchResult]
                        : [],
                      purchaseHistory: row.purchaseHistory
                        ? [...row.purchaseHistory]
                        : [],
                    }));

                    reset({ rows: resetData });
                    setExtraKeys([...originalExtraKeysRef.current]);
                    setExtraKeyLabels({ ...originalExtraKeyLabelsRef.current });

                    setIsEditing(false);
                    setRowSelection({}); // 체크박스 선택 상태 초기화
                  }}
                >
                  취소하기
                </Button>
                <Button
                  type="submit"
                  className="bg-dark-red-60 hover:bg-dark-red-60/80"
                  // disabled={!formState.isDirty}
                >
                  적용하기
                </Button>
              </div>
            </>
          ) : (
            <div className="absolute right-0 -top-10 flex items-center justify-end gap-4">
              <Button
                className="bg-dark-red-60 hover:bg-dark-red-60/80"
                onClick={() => {
                  // 편집 모드 진입 (원본 데이터는 보존)
                  setIsEditing(true);
                }}
                disabled={isExcelDownloading}
              >
                수정하기
              </Button>
            </div>
          )}

          {/* 단일 테이블 - Sticky Header 방식 */}
          <div
            ref={tableContainerRef}
            className="max-h-[70vh] overflow-auto"
            style={{
              scrollBehavior: "auto",
              contain: "layout style paint",
              willChange: "scroll-position",
              transform: "translateZ(0)",
              backfaceVisibility: "hidden",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              overscrollBehavior: "contain",
              scrollbarGutter: "stable both-edges",
            }}
          >
            <Table className="table-fixed w-full">
              <colgroup>
                {table.getAllColumns().map((col) => (
                  <col
                    key={col.id}
                    style={{ width: `${col.columnDef.size}px` }}
                  />
                ))}
              </colgroup>

              {/* Sticky Header */}
              <TableHeader className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="px-2 text-sm text-white bg-dark-red-60 first:rounded-tl-md last:rounded-tr-md overflow-hidden"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>

              {/* 가상화된 Body */}
              <TableBody>
                {paddingTop > 0 && (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      style={{ height: `${paddingTop}px` }}
                    />
                  </tr>
                )}
                {virtualRows.length > 0 ? (
                  virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return (
                      <React.Fragment key={row.original.uuid}>
                        <TableRow
                          data-index={virtualRow.index}
                          data-state={row.getIsSelected() && "selected"}
                          className={cn(
                            row.getIsSelected() && "bg-green/20",
                            !row.original.extras?.highlight &&
                              (row.index % 2 === 0
                                ? "bg-white"
                                : "bg-light-gray")
                          )}
                          style={{
                            height: "68px",
                            minHeight: "68px",
                            maxHeight: "68px",
                            display: "table-row",
                            boxSizing: "border-box",
                            backgroundColor: row.getIsSelected()
                              ? "rgba(34, 197, 94, 0.2)"
                              : row.original.extras?.highlight
                                ? "#fef3c7"
                                : undefined,
                          }}
                        >
                          {row.getVisibleCells().map((cell) => {
                            // 규격서 테이블에 있는 편집 가능한 필드들
                            const editableFields = [
                              "product_name",
                              "product_id",
                              "specification",
                              "manufacturer",
                              "quantity",
                              "unit",
                            ];

                            const accessorKey = cell.column.id;
                            const isEditableField =
                              editableFields.includes(accessorKey) ||
                              accessorKey.startsWith("extra."); // extra 컬럼도 포함

                            // 🔥 Original Data vs Latest Data 비교하여 변경 여부 확인 (편집 모드가 아닐 때만)
                            let isChanged = false;
                            if (
                              !isEditing &&
                              isEditableField &&
                              originalRows.length > 0
                            ) {
                              // Original Data에서 매칭되는 행 찾기
                              const originalRow = originalRows.find(
                                (origRow: CombinedRow) => {
                                  // UUID로 매칭 (가장 정확)
                                  return origRow.uuid === row.original.uuid;
                                }
                              );

                              let originalValue, currentValue;
                              if (accessorKey.startsWith("extra.")) {
                                const extraKey = accessorKey.replace(
                                  "extra.",
                                  ""
                                );
                                originalValue = originalRow?.extra?.[extraKey];
                                currentValue = row.original.extra?.[extraKey];
                              } else {
                                originalValue =
                                  originalRow?.[
                                    accessorKey as keyof CombinedRow
                                  ];
                                currentValue =
                                  row.original[
                                    accessorKey as keyof CombinedRow
                                  ];
                              }

                              isChanged =
                                String(currentValue ?? "") !==
                                String(originalValue ?? "");
                            }

                            return (
                              <TableCell
                                key={cell.id}
                                style={{
                                  height: "68px",
                                  minHeight: "68px",
                                  maxHeight: "68px",
                                  boxSizing: "border-box",
                                  ...(isChanged
                                    ? {
                                        border: "1px solid #A76D6E",
                                      }
                                    : {}),
                                }}
                                className="py-[10px] px-2 overflow-hidden"
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {expandedRowIds[row.id] && (
                          <TableRow>
                            <TableCell
                              colSpan={table.getAllColumns().length}
                              className="p-0"
                            >
                              <div className="flex items-center justify-between gap-2 text-sm text-gray-A font-semibold">
                                <EstimateAlternativeProductTable
                                  id={row.id}
                                  index={row.index}
                                  googleSearchResult={
                                    row.original.googleSearchResult
                                  }
                                  purchaseHistory={row.original.purchaseHistory}
                                />
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      검색 결과가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : null}
                {paddingBottom > 0 && (
                  <tr>
                    <td
                      colSpan={table.getAllColumns().length}
                      style={{ height: `${paddingBottom}px` }}
                    />
                  </tr>
                )}
                {isEditing && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center">
                      <div className="h-8 flex items-center justify-end pr-3">
                        <PlusCircle
                          className="size-4 cursor-pointer"
                          onClick={handleAddRow}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4">
          {(Object.keys(BadgeType) as BadgeKeys[]).map((key) => (
            <EstimateBadge type={key} key={key} />
          ))}
        </div>

        <SearchProductDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          product_id={selectedProductId}
          onApply={handleApplyProduct}
        />
      </form>
    </Form>
  );
}
